import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  AssociatedTestTemplateCreateFailure,
  AssociatedTestTemplateCreateInput,
  AssociatedTestTemplateMutationCreateArgs,
  CreateAssociatedTestTemplateMutation,
} from "@/graphql/codegen/graphql"
import { CreateAssociatedTestTemplateDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateAssociatedTestTemplateReturn {
  create: (input: AssociatedTestTemplateCreateInput) => Promise<void>
  isCreating: boolean
  error?: CombinedError
}

export const useCreateAssociatedTestTemplate = (onSuccess: () => void): UseCreateAssociatedTestTemplateReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    CreateAssociatedTestTemplateMutation,
    AssociatedTestTemplateMutationCreateArgs
  >(CreateAssociatedTestTemplateDocument)

  const create = async (input: AssociatedTestTemplateCreateInput) => {
    const { data, error } = await executeMutation({ input })
    const output = data?.associatedTestTemplate.create
    const hasError = error || output?.__typename === "AssociatedTestTemplateCreateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message:
          error?.message ??
          (output as AssociatedTestTemplateCreateFailure).error.message ??
          "Failed to create associated test template.",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      message: "Associated test template successfully created.",
    })
    onSuccess()
  }

  return {
    create,
    isCreating: fetching,
    error,
  }
}
